import React from 'react';
import c from 'classnames';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import camelCase from 'lodash/camelCase';
import ddt from '@wix/da-ddt';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { Props as WidgetBoxProps } from '../WidgetBox';
import { ZoneName } from '../../types/zones';
import { ModuleName } from '../../types/modules';
import { WidgetConfig } from '../../types/widget';
import ZoneContext from '../../context/ZoneContext/ZoneContext';
import AddWidget from '../AddWidget';
import { useZoneModules } from '../../hooks/useZone';
import usePrivileges from '../../hooks/usePrivileges';
import { filterAlreadyInstalledAndCoreRestrictedModules, getInstallableWidgets } from './utils';

import s from './Zone.scss';

export interface Props {
  className?: string;
  name: ZoneName;
  direction?: 'row' | 'column';
  withStaticAddWidget?: boolean; // this makes add widget gizmo always visible (not only on hover)
  widgetConfigs: Partial<Record<ModuleName, WidgetConfig>>;
  locked?: boolean;
}

const Zone: React.FC<Props> = ({
  className,
  withStaticAddWidget,
  widgetConfigs,
  name,
  direction = 'column',
  locked,
}) => {
  const { canAddModule } = usePrivileges();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { installedModules, installableModules } = useZoneModules(name);
  const installableModulesFiltered = filterAlreadyInstalledAndCoreRestrictedModules(
    installableModules,
    installedModules
  );

  const installableWidgets = getInstallableWidgets(widgetConfigs, installableModulesFiltered);

  ddt.log('grusers', 'Installed modules:', { zone: name, installedModules });

  if (!installedModules.length && !canAddModule) {
    return null;
  }

  return (
    <div
      className={c(
        s['root'],
        withStaticAddWidget && s['static-add-widget'],
        className,
        s[direction]
      )}
      key={name}
    >
      <ZoneContext.Provider value={{ name, direction, locked }}>
        {installedModules.map((module, index) => {
          const isLast = index === installedModules.length - 1;
          const widgetConfig = widgetConfigs[camelCase(module.name)];
          if (!widgetConfig) {
            return null;
          }

          ddt.log('grusers', 'Widget to render:', { zone: name, widgetConfig });

          const isColumn = direction === 'column';

          const {
            viewComponent: WidgetComponent,
            configPopoverComponent,
            autoShowWidgetConfig,
            confirmRemove,
            isWide,
          } = widgetConfig;

          const widgetBoxProps: Partial<WidgetBoxProps> = {
            configPopoverComponent,
            autoShowWidgetConfig,
            confirmRemove: confirmRemove ? confirmRemove({ t, dispatch }) : undefined,
            className: s['item'],
          };

          return (
            <ErrorBoundary debugComponent="Zone" key={index}>
              <BiLoggerContextProvider value={{ sectionname: `${module.name}_widget` }}>
                {WidgetComponent && (
                  <WidgetComponent
                    key={index}
                    moduleId={module.id}
                    widgetBoxProps={widgetBoxProps}
                    isWide={isWide}
                  />
                )}
              </BiLoggerContextProvider>
              {installableWidgets.length > 0 && !isLast && !locked && isColumn && (
                <AddWidget
                  key={`add-after-${index}`}
                  position={index + 1}
                  installableWidgets={installableWidgets}
                />
              )}
            </ErrorBoundary>
          );
        })}
      </ZoneContext.Provider>
    </div>
  );
};
Zone.displayName = 'Zone';

export default Zone;
